import { Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

import { getPropsFrom, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import { extractColorProperties, getColors, getTextColorsPropertiesStyle } from '../../../../utils/colors';

interface CheckboxItemProperties {
  text: string;
  style: string;
  isChecked: string;
}

@Component({
  selector: 'lfe-checkbox-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './checkbox-item-form.component.html',
  standalone: true,
  styleUrl: './checkbox-item-form.component.css'
})
export class CheckboxItemFormComponent extends InteractiveItemComponent<CheckboxItemProperties> implements OnInit {
  formControl: FormControl<boolean | null> | undefined;
  properties: InteractiveItemProperties & CheckboxItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: '',
    isChecked: 'False'
  };
  disabled = false;

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `checkbox_${this.currentId()}`);

  currentTextColor = signal('');
  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));

  hasInteracted = signal(false);

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = this.parseStringToBoolean(this.properties.isChecked);
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & CheckboxItemProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'] || '';
    const id = props['ID'] || '';
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const isChecked = props['IsChecked'] || 'False';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));
    this.currentTextColor.set(textColor);

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      resultValue,
      resultUser,
      resultTime,
      text,
      style,
      isChecked,
      textBgColor,
      isGenericRepeatItem
    };
  }

  private parseStringToBoolean(value: string): boolean {
    return value === 'True';
  }

  private formatBooleanForStorage(value: boolean): string {
    return value ? 'True' : 'False';
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.hasInteracted()) {
      upsertChildren(element, {
        IsChecked: this.properties.isChecked
      });
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  onCheckboxChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.checked;

    this.formControl?.setValue(value);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.isChecked = this.formatBooleanForStorage(value);

    this.properties.resultValue = this.formatBooleanForStorage(value);
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().loginName;

    this.hasInteracted.set(true);
  }
}
